import { Button } from '@ctw/shared/components/button';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { notify } from '@ctw/shared/components/toast';
import type { usePatientSummaries } from '@ctw/shared/content/summarization/use-patient-summaries';
import { useZuiContext } from '@ctw/shared/context/zui-provider';
import { tw } from '@ctw/shared/utils/tailwind';
import { faExternalLink } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

const SUMMARY_LIMIT_APPROACHING_THRESHOLD = 0.8 as const;

interface UserPatientSummarizationState {
  summariesAllotted: number;
  summariesUsed: number;
  summarizationLimitApproaching: boolean;
  summarizationLimitReached: boolean;

  generateSummary: () => Promise<ReturnType<typeof usePatientSummaries> | null>;
  reloadUsage: () => void;
}

const UserPatientSummarizationContext = createContext<UserPatientSummarizationState | undefined>(
  undefined,
);

interface UserPatientSummarizationProviderProps extends PropsWithChildren {
  fakeSummariesAllotted: number;
  fakeSummariesUsed: number;
}

export const UserPatientSummarizationProvider = ({
  fakeSummariesAllotted,
  fakeSummariesUsed,
  children,
}: UserPatientSummarizationProviderProps) => {
  const { navigate } = useZuiContext();
  // TODO: Disable API calls is feature flag is disabled
  // const hasSummarizationV2UiRefreshFeature = useFeatureFlag('ctw-summarization-v2-ui-refresh');

  // TODO: replace with real values once the usage APIs are complete
  const [summarizationUsage, _setSummarizationUsage] = useState<
    Pick<UserPatientSummarizationState, 'summariesAllotted' | 'summariesUsed'>
  >({
    summariesAllotted: fakeSummariesAllotted,
    summariesUsed: fakeSummariesUsed,
  });

  const generateSummary = useCallback(
    () =>
      new Promise<ReturnType<typeof usePatientSummaries> | null>((resolve) => {
        setTimeout(() => {
          notify({
            type: 'success',
            title: 'Your patient summary is ready to view!',
            body: (
              <div className={tw`flex items-center justify-start gap-3`}>
                <Button type="button" variant="primary" onClick={() => navigate('summary')}>
                  <div className={tw`flex items-center space-x-2`}>
                    <div className={tw`whitespace-nowrap text-sm`}>View Patient Summary</div>
                    <FontAwesomeIcon icon={faExternalLink} className={tw`h-4 w-4`} />
                  </div>
                </Button>
              </div>
            ),
          });
          resolve(null);
        }, 300);
      }),
    [navigate],
  );

  const reloadUsage = useCallback(() => {
    // TODO: implement once the usage APIs are complete
  }, []);

  const summarizationState = useMemo(() => {
    const summarizationLimitReached =
      summarizationUsage.summariesUsed >= summarizationUsage.summariesAllotted;
    const summarizationLimitApproaching =
      !summarizationLimitReached &&
      summarizationUsage.summariesUsed / summarizationUsage.summariesAllotted >=
        SUMMARY_LIMIT_APPROACHING_THRESHOLD;

    return {
      summariesAllotted: summarizationUsage.summariesAllotted,
      summariesUsed: summarizationUsage.summariesUsed,
      summarizationLimitApproaching,
      summarizationLimitReached,
      generateSummary,
      reloadUsage,
    };
  }, [
    summarizationUsage.summariesAllotted,
    summarizationUsage.summariesUsed,
    generateSummary,
    reloadUsage,
  ]);

  const summarizationUsageReady = summarizationUsage !== null;

  return (
    <UserPatientSummarizationContext.Provider value={summarizationState}>
      {summarizationUsageReady ? children : <LoadingSpinner centered={true} />}
    </UserPatientSummarizationContext.Provider>
  );
};

export const useUserPatientSummarizationContext = () => {
  const context = useContext(UserPatientSummarizationContext);

  if (!context) {
    throw new Error(
      'useUserPatientSummarizationContext must be used within a UserPatientSummarizationProvider',
    );
  }

  return context;
};
